import Keyboard from "simple-keyboard";
import germanLayout from "simple-keyboard-layouts/build/layouts/german";
import "simple-keyboard/build/css/index.css";

const INPUT_SELECTOR =
  'input[type="text"], input[type="search"], input[type="password"], input[type="email"], input[type="number"], textarea';

let keyboardInstance = null;
let wrapperEl = null;
let activeInput = null;
let enabled = false;
let physicalKeyboardDetected = false;
let bound = false;
// When simple-keyboard re-renders the layout (e.g. shift/caps toggle via setOptions),
// the old button DOM is destroyed. On touch devices this causes a stray pointerdown
// event whose target is the page content behind the keyboard (e.g. a DIV.row), not a
// keyboard button. Because wrapperEl.contains() returns false for that target,
// onPointerDown would incorrectly call hideKeyboard(). This flag suppresses that
// stray event for one animation frame during layout transitions.
let suppressPointerDown = false;

function shouldEnable() {
  if (physicalKeyboardDetected) return false;
  const hasTouch = navigator.maxTouchPoints > 0;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return hasTouch && !isMobile;
}

// handle HTMX body swaps that detach the DOM
function createKeyboard() {
  if (wrapperEl && document.body.contains(wrapperEl)) return;

  // Clean up detached wrapper from previous hx-boost navigation
  keyboardInstance = null;
  wrapperEl = null;

  wrapperEl = document.createElement("div");
  wrapperEl.className = "virtual-keyboard-wrapper";
  document.body.appendChild(wrapperEl);

  // Create a separate inner element for simple-keyboard to manage,
  // so its re-renders (e.g. layout changes) don't reset wrapperEl's classes.
  const keyboardEl = document.createElement("div");
  keyboardEl.className = "simple-keyboard";
  wrapperEl.appendChild(keyboardEl);

  keyboardInstance = new Keyboard(keyboardEl, {
    ...germanLayout,
    theme: "hg-theme-default virtual-keyboard-theme",
    preventMouseDownDefault: true,
    mergeDisplay: true,
    display: {
      "{enter}": "↵",
      "{bksp}": "⌫",
      "{shift}": "⇧",
      "{lock}": "⇪",
      "{space}": " ",
      "{tab}": "↹",
    },
    onChange: (input) => {
      if (!activeInput) return;
      activeInput.value = input;
      activeInput.dispatchEvent(new Event("input", { bubbles: true }));
    },
    onKeyPress: (button) => {
      // hideKeyboard() clears activeInput, so onChange cannot append a newline
      if (
        button === "{enter}" &&
        activeInput &&
        activeInput.tagName !== "TEXTAREA"
      ) {
        const input = activeInput;
        hideKeyboard();
        if (input.form) {
          input.form.requestSubmit();
        } else {
          input.dispatchEvent(
            new KeyboardEvent("keyup", { key: "Enter", bubbles: true }),
          );
        }
        return;
      }
      if (button === "{shift}" || button === "{lock}") {
        suppressPointerDown = true;
        const currentLayout = keyboardInstance.options.layoutName;
        keyboardInstance.setOptions({
          layoutName: currentLayout === "default" ? "shift" : "default",
        });
        requestAnimationFrame(() => {
          suppressPointerDown = false;
        });
      }
    },
  });
}

function showKeyboard(input) {
  if (!enabled || !keyboardInstance) return;
  activeInput = input;
  keyboardInstance.setInput(input.value || "");
  wrapperEl.classList.add("visible");
  document.body.classList.add("virtual-keyboard-active");
}

function hideKeyboard() {
  if (!wrapperEl) return;
  activeInput = null;
  wrapperEl.classList.remove("visible");
  document.body.classList.remove("virtual-keyboard-active");
}

function isTargetInput(el) {
  if (!el || !el.matches) return false;
  if (!el.matches(INPUT_SELECTOR)) return false;
  // filters out hidden inputs
  if (el.type === "hidden") return false;
  // filters out the payment calculator (which has its own keypad)
  if (el.closest(".payment-calculator")) return false;
  return true;
}

function onFocusIn(event) {
  if (!enabled) return;
  if (isTargetInput(event.target)) {
    showKeyboard(event.target);
  }
}

function onPointerDown(event) {
  if (!wrapperEl || !activeInput) return;
  if (suppressPointerDown) return;
  if (wrapperEl.contains(event.target)) return;
  if (event.target === activeInput) return;
  if (isTargetInput(event.target)) return;
  hideKeyboard();
}

function onPhysicalKeydown(event) {
  if (!event.isTrusted) return;
  physicalKeyboardDetected = true;
  enabled = false;
  hideKeyboard();
  document.removeEventListener("keydown", onPhysicalKeydown);
}

export function bindVirtualKeyboard() {
  if (!bound) {
    enabled = shouldEnable();
    if (!enabled) return;

    bound = true;
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onPhysicalKeydown);
  }

  if (!enabled) return;

  // Re-create keyboard DOM if it was detached by an HTMX body swap
  createKeyboard();

  if (isTargetInput(document.activeElement)) {
    showKeyboard(document.activeElement);
  }
}
